///////////////////////////////////////////////////////////////////////////////////////
//                          Copyright (c) 2020 - 2023
//                           Coyote Logistics L.L.C.
//                         All Rights Reserved Worldwide
//
//WARNING:  This program (or document) is unpublished, proprietary
//property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
//Unauthorized reproduction, distribution or disclosure of this program
//(or document), or any program (or document) derived from it is
//prohibited by State and Federal law, and by local law outside of the U.S.
///////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect } from "react";
import Ripples from "react-ripples";
import { useLocation, useHistory } from "react-router-dom";
import { orderBy } from "lodash";

import AddCircleIcon from "./icons/AddCircleIcon";
import HomeIcon from "./icons/HomeIcon";
import PendingBidsIcon from "./icons/PendingBidsIcon";
import SearchIcon from "./icons/SearchIcon";
import { division, order, userRole } from "../helpers/types";
import AdminIcon from "./icons/AdminIcon";
import NotificationIcon from "./icons/NotificationIcon";
import Tooltip from "../components/Tooltip";
import NotificationList from "./notifications/NotificationList";
import messages from "../helpers/messages";
import SettingsIcon from "./icons/SettingsIcon";
import AnalyticsIcon from "./icons/AnalyticsIcon";
import CompassIcon from "./icons/CompassIcon";
import LogoutIcon from "./icons/LogoutIcon";

function NavBar({
  role,
  isAdmin,
  userDivision, 
  notifications,
  onOpenLocationSearch = () => {},
  isLocationSearchOpen = false
}) {
  const pathname = useLocation().pathname;
  const { push, replace } = useHistory();
  const readNotifications = orderBy(
    notifications.filter(x => x.isPreviouslySeenByUser === true),
    "createDateUTC",
    order.descending
  );
  const unreadNotifications = orderBy(
    notifications.filter(x => x.isPreviouslySeenByUser === false),
    "createDateUTC",
    order.descending
  );

  useEffect(() => {
    const route = pathname.split("/")[1];
    if (
      role &&
      route === "pending-bids" &&
      (role === userRole.salesRep ||
        role === userRole.carrierRelationsRepresentative ||
        role === userRole.procurementRepresentative)
    ) {
      replace("/");
    }
  }, [role, replace, pathname, isAdmin]);

  return (
    <nav id="nav" data-testid="nav">
      <ul className="flex flex-col full-height text-center mb-0">
        <li
          className={`nav-item pos-relative ${
            pathname === "/" || pathname === "/grid-view" ? "selected-item" : ""
          }`}
        >
          <Ripples
            color="#394351"
            className="full-width full-height flex justify-center items-center"
            onClick={() => push("/")}
          >
            <span>
              <HomeIcon aria-hidden="true" />
              <div>{messages.home.defaultMessage}</div>
            </span>
          </Ripples>
        </li>
        {role !== userRole.salesRep &&
          role !== userRole.carrierRelationsRepresentative &&
          role !== userRole.procurementRepresentative && (
            <li
              className={`nav-item pos-relative ${
                pathname === "/pending-bids" ? "selected-item" : ""
              }`}
            >
              <Ripples
                color="#394351"
                className="full-width full-height flex justify-center items-center"
                onClick={() => push("/pending-bids")}
              >
                <span>
                  <PendingBidsIcon aria-hidden="true" />
                  <div>{messages.pending.defaultMessage}</div>
                </span>
              </Ripples>
            </li>
          )}
        <li
          className={`nav-item pos-relative ${
            pathname === "/search" ? "selected-item" : ""
          }`}
        >
          <Ripples
            color="#394351"
            className="full-width full-height flex justify-center items-center"
            onClick={() => push("/search")}
          >
            <span>
              <SearchIcon aria-hidden="true" />
              <div>{messages.search.defaultMessage}</div>
            </span>
          </Ripples>
        </li>
        {userDivision !== division.eu && (
          <li
            className={`nav-item pos-relative ${
              pathname === "/new-bid" ? "selected-item" : ""
            }`}
          >
            <Ripples
              color="#394351"
              className="full-width full-height flex justify-center items-center"
              onClick={() => push("/new-bid")}
            >
              <span>
                <AddCircleIcon aria-hidden="true" />
                <div>{messages.newBid.defaultMessage}</div>
              </span>
            </Ripples>
          </li>
        )}
        <li
          className={`nav-item pos-relative ${
            pathname === "/settings" ? "selected-item" : ""
          }`}
        >
          <Ripples
            color="#394351"
            className="full-width full-height flex justify-center items-center"
            onClick={() => push("/settings")}
          >
            <span>
              <SettingsIcon height={20} width={20} />
              <div>{messages.settings.defaultMessage}</div>
            </span>
          </Ripples>
        </li>
        <li
          className={`nav-item pos-relative ${
            pathname === "/analytics" ? "selected-item" : ""
          }`}
        >
          <Ripples
            color="#394351"
            className="full-width full-height flex justify-center items-center"
            onClick={() => push("/analytics")}
          >
            <span>
              <AnalyticsIcon height={16} width={16} />
              <div>{messages.analytics.defaultMessage}</div>
            </span>
          </Ripples>
        </li>
        <li
          className={`nav-item pos-relative ${
            isLocationSearchOpen ? "selected-item" : ""
          }`}
        >
          <Ripples
            color="#394351"
            className="full-width full-height flex justify-center items-center"
            onClick={onOpenLocationSearch}
          >
            <span>
              <CompassIcon height={16} width={16} />
              <div>{messages.locations.defaultMessage}</div>
            </span>
          </Ripples>
        </li>
        {isAdmin && (
          <li
            className={`nav-item pos-relative ${
              pathname === "/admin" ? "selected-item" : ""
            }`}
          >
            <Ripples
              color="#394351"
              className="full-width full-height flex justify-center items-center"
              onClick={() => push("/admin")}
            >
              <span>
                <AdminIcon aria-hidden="true" />
                <div>{messages.admin.defaultMessage}</div>
              </span>
            </Ripples>
          </li>
        )}
        <div>
          <Tooltip
            content={
              <NotificationList
                notifications={
                  unreadNotifications.length < 8
                    ? [
                        ...unreadNotifications,
                        ...readNotifications.slice(
                          0,
                          8 - unreadNotifications.length
                        )
                      ]
                    : unreadNotifications
                }
                isFlyout={true}
              />
            }
            trigger="click"
            className="flyout-window notifications no-focus"
            interactive
            maxWidth={434}
          >
            <li
              className={`nav-item pos-relative ${
                pathname === "/notifications" ? "selected-item" : ""
              }`}
              data-testid="notification-menu"
              id="notification-menu-icon"
            >
              <Ripples
                color="#394351"
                className="full-width full-height flex justify-center items-center"
              >
                {unreadNotifications.length > 0 && (
                  <span className="pos-absolute round m-2 unread-notification">
                    {unreadNotifications.length > 9
                      ? "9+"
                      : unreadNotifications.length}
                  </span>
                )}
                <span style={{ zIndex: 9999 }}>
                  <NotificationIcon aria-hidden="true" />
                </span>
              </Ripples>
            </li>
          </Tooltip>
        </div>
        <li
            className={`nav-item pos-relative ${
              pathname === "/logout" ? "selected-item" : ""
            }`}
          >
            <Ripples
              color="#394351"
              className="full-width full-height flex justify-center items-center"
              onClick={() => push("/logout")}
            >
              <span>
                <LogoutIcon aria-hidden="true" />
                <div>{messages.logout.defaultMessage}</div>
              </span>
            </Ripples>
          </li>
      </ul>
    </nav>
  );
}

export default NavBar;
